import {
  Button,
  ButtonColorEnum,
  ButtonSizeEnum,
  DrawerForm,
  FontColorEnum,
  FormField,
  FormHint,
  FormInput,
  FormLabel,
  LockIcon,
  MailIcon,
} from '@acceso/design-system';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { User } from '../../types/types';

interface ProfileDrawerFormProps {
  isOpen: boolean;
  isMutating?: boolean;
  data?: Partial<User> | null;
  onChangePassword?: () => void;
  onChangeEmail?: () => void;
  onSubmit?: (data: any, reset: () => void) => void;
  onCancel?: () => void;
}

export function ProfileDrawerForm({
  isOpen,
  isMutating = false,
  data,
  onChangePassword,
  onChangeEmail,
  onSubmit,
  onCancel,
}: ProfileDrawerFormProps) {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<Partial<User>>();

  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    handleReset();

    if (data) {
      if (data.id) {
        setValue('id', data.id);
      }

      setValue('first_name', data.first_name);
      setValue('last_name', data.last_name);
      setValue('email', data.email);
      setValue('username', data.username);
    }
  }, [data, setValue, handleReset]);
  return (
    <DrawerForm
      title={t('Editar perfil')}
      isOpen={isOpen}
      isMutating={isMutating}
      onSubmit={handleSubmit(data => onSubmit && onSubmit(data, handleReset))}
      onCancel={() => onCancel && onCancel()}
      labels={{
        submit: t('Guardar'),
        cancel: t('Cancelar'),
      }}
    >
      <div className="w-full space-y-6">
        <FormField>
          <FormLabel htmlFor="first_name">{t('Nombre')}</FormLabel>
          <FormInput
            id="first_name"
            hasError={Boolean(errors.first_name)}
            placeholder={t('Lola')}
            {...register('first_name', {
              required: t('El nombre es requerido'),
              maxLength: {
                value: 50,
                message: t('El nombre debe ser menor de 50 caracteres'),
              },
              pattern: {
                value: /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/,
                message: t('El nombre debe contener solo letras'),
              },
            })}
          />
          {errors.first_name && (
            <FormHint color={FontColorEnum.Error20}>{errors.first_name.message}</FormHint>
          )}
        </FormField>

        <FormField>
          <FormLabel htmlFor="last_name">{t('Apellidos')}</FormLabel>
          <FormInput
            id="last_name"
            hasError={Boolean(errors.last_name)}
            placeholder={t('García')}
            {...register('last_name', {
              required: t('El apellido es requerido'),
              maxLength: {
                value: 50,
                message: t('El apellido debe ser menor de 50 caracteres'),
              },
              pattern: {
                value: /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/,
                message: t('El apellido debe contener solo letras'),
              },
            })}
          />
          {errors.last_name && (
            <FormHint color={FontColorEnum.Error20}>{errors.last_name.message}</FormHint>
          )}
        </FormField>

        <FormField>
          <FormLabel htmlFor="username">{t('Nombre de usuario')}</FormLabel>
          <FormInput {...register('username', { required: true })} id="username" disabled />
        </FormField>

        <FormField>
          <FormLabel htmlFor="email">{t('Email')}</FormLabel>
          <FormInput {...register('email', { required: true })} id="email" disabled />
        </FormField>

        <Button
          onClick={() => onChangeEmail?.()}
          size={ButtonSizeEnum.LG}
          color={ButtonColorEnum.DEFAULT}
          leftIcon={() => <MailIcon />}
        >
          {t('Cambiar email')}
        </Button>

        <FormField>
          <FormLabel htmlFor="password">{t('Contraseña')}</FormLabel>
          <FormInput id="password" disabled value={'*********'} />
        </FormField>

        <Button
          onClick={() => onChangePassword?.()}
          size={ButtonSizeEnum.LG}
          color={ButtonColorEnum.DEFAULT}
          leftIcon={() => <LockIcon />}
        >
          {t('Cambiar contraseña')}
        </Button>
      </div>
    </DrawerForm>
  );
}
